import m from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {Icon} from '@bitstillery/common/components'
import {$t} from '@bitstillery/common/app'
import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const dialogMeta = {
    tier: 'layout',
    summary: 'Modal overlay: a titled panel over a dimmed page, with an optional footer.',
    when_to_use:
        'The portal shows a CMS dialog in exactly one place: the `cms-popup` fragment, which the content frame renders once a day per visitor (the same contract as the offer popup) and only while that fragment is published. So a `Dialog` belongs in `cms-popup` and nowhere else — on a page it would cover the page. Give it a `title`, an `Image` and a `Paragraph` for the body, and a `Button` in `footer` for the call to action. `onclose={popup_dismiss}` is not optional: it is what draws the close button and what stores the dismissal, and a popup without it re-opens on every page load. Following the call to action dismisses the popup too — the portal wires that up, so do not add an `onclick` for it.',
    props: "title?: string — Heading in the title bar.\nhideTitle?: boolean — Drop the title bar; the close button floats over the body instead (for a popup that is one full-bleed image).\nfooter?: children — The call-to-action row, usually a single `Button`.\nonclose?: () => void — Runs on close (button, backdrop click, Escape). In `cms-popup` this is `popup_dismiss`.\nsize?: 'default' | 'wide' — `wide` for a body that is content to work through rather than a question to answer.\ntype?: 'info' | 'default' | 'warning' | 'danger' — Colours the title bar; `info` when omitted.",
    showcase_skipped: 'it covers the whole viewport, and this page is a list of components',
    example: `
    <Dialog
        footer={<Button link="/our-range" text="See the range" type="info" variant="cta" />}
        onclose={popup_dismiss}
        title="New this autumn"
    >
        <Image alt="Autumn bottles on a bar" url="https://media.example/popup/autumn.jpg" />
        <Paragraph>Fifty new bottles landed this week.</Paragraph>
    </Dialog>
    `,
} satisfies CmsComponentMeta

export interface DialogAttrs {
    body?: () => m.Children
    className?: string
    icon?: string
    footer?: m.Children
    onclose?: () => unknown
    onopen?: () => unknown
    loading?: boolean | undefined
    persistent?: boolean
    /** `wide` for a body that is content to work through rather than a question to answer. */
    size?: 'default' | 'wide' | undefined
    type?: 'info' | 'default' | 'warning' | 'danger' | (string & {})
    title?: string | undefined
    /** When true, hide the title bar; close button is shown as floating in the top-right */
    hideTitle?: boolean
}

export class Dialog extends MithrilComponent<DialogAttrs> {
    is_hidden = true
    close_callback: (() => unknown) | undefined
    escape_key_listener: (e: KeyboardEvent) => void = () => {}

    constructor(vnode: m.Vnode<DialogAttrs>) {
        super()
        this.close_callback = vnode.attrs.onclose
    }

    override oncreate(vnode: m.Vnode<DialogAttrs>) {
        if (vnode.attrs.onopen) {
            vnode.attrs.onopen()
            m.redraw()
        }

        function listener(this: Dialog, e: KeyboardEvent) {
            if (!this.is_hidden && e.key === 'Escape' && this.close_callback) {
                this.close_callback()
                m.redraw()
            }
        }

        this.escape_key_listener = listener.bind(this)
        document.body.addEventListener('keyup', this.escape_key_listener)
    }

    override onremove() {
        document.body.removeEventListener('keyup', this.escape_key_listener)
    }

    view(vnode: m.Vnode<DialogAttrs>) {
        this.is_hidden = vnode.attrs.className?.includes('hidden') || false
        return (
            <div
                className={classes(
                    'c-dialog',
                    vnode.attrs.className,
                    `size-${vnode.attrs.size ?? 'default'}`,
                    `type-${vnode.attrs.type ? vnode.attrs.type : 'info'}`,
                )}
                onmouseup={(e: MouseEvent) => {
                    e.stopPropagation()
                    if (!vnode.attrs.loading && vnode.attrs.onclose) {
                        vnode.attrs.onclose()
                    }
                }}
            >
                <div
                    className={classes('dialog', vnode.attrs.hideTitle && 'dialog--no-title')}
                    onmouseup={(e: MouseEvent) => e.stopPropagation()}
                >
                    {!vnode.attrs.hideTitle && (
                        <div class='title'>
                            <div className='title-text'>{vnode.attrs.title}</div>
                            {vnode.attrs.onclose && (
                                <Icon
                                    className='btn-close'
                                    disabled={vnode.attrs.loading || !vnode.attrs.onclose}
                                    name='close'
                                    onclick={() => {
                                        if (vnode.attrs.onclose) {
                                            vnode.attrs.onclose()
                                        }
                                    }}
                                    size='l'
                                    type='unset'
                                    tip={$t('dialog.close')}
                                    variant='toggle'
                                />
                            )}
                        </div>
                    )}
                    {vnode.attrs.hideTitle && vnode.attrs.onclose && (
                        <Icon
                            className='btn-close btn-close--floating'
                            disabled={vnode.attrs.loading || !vnode.attrs.onclose}
                            name='close'
                            onclick={() => {
                                if (vnode.attrs.onclose) {
                                    vnode.attrs.onclose()
                                }
                            }}
                            size='l'
                            type='unset'
                            tip={$t('dialog.close')}
                            variant='toggle'
                        />
                    )}
                    <div className='body'>{vnode.children}</div>

                    {vnode.attrs.footer && <div className='footer'>{vnode.attrs.footer}</div>}
                </div>
            </div>
        )
    }
}
